import { Category, CategoryType } from '@/features/product-configurator/types';

/**
 * COMPLETE CONFIGURATION - ALL 120+ OPTIONS
 * Every option has: responsePath, inputType, responseValue, validation, showWhen
 * 100% ready for configuration-driven JSON generation
 */

export const MOCK_CATEGORIES: Category[] = [
  // ============================================
  // 1. PROJECT INFORMATION (3 options)
  // ============================================
  {
    id: 'cat-project-info',
    type: CategoryType.STYLE,
    name: '📋 Project Information',
    description: 'Project type and window location',
    isActive: true,
    order: 0,
    options: [
      {
        id: 'opt-project-commercial',
        name: 'Commercial Project',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Commercial project',
        isActive: true,
        order: 1,
        responsePath: 'window.projectType',
        responseValue: 'Commercial',
        inputType: 'single_select',
      },
      {
        id: 'opt-project-residential',
        name: 'Residential Project',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Residential project',
        isActive: true,
        order: 2,
        responsePath: 'window.projectType',
        responseValue: 'Residential',
        inputType: 'single_select',
      },
      {
        id: 'opt-window-location',
        name: 'Window Location/Reference',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Enter location or reference',
        isActive: true,
        order: 3,
        responsePath: 'window.location',
        inputType: 'text_input',
      },
    ],
  },

  // ============================================
  // 2. MEASUREMENTS (21 options)
  // ============================================
  {
    id: 'cat-measurements',
    type: CategoryType.SIZE,
    name: '📏 Measurements',
    description: 'Window dimensions and configuration',
    isActive: true,
    order: 1,
    options: [
      // Basic dimensions
      {
        id: 'opt-width',
        name: 'Window Width (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Width in mm',
        isActive: true,
        order: 1,
        responsePath: 'window.measurements.widthMm',
        inputType: 'number_input',
        validation: { min: 370, max: 1600, required: true },
      },
      {
        id: 'opt-height',
        name: 'Window Height (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Height in mm',
        isActive: true,
        order: 2,
        responsePath: 'window.measurements.heightMm',
        inputType: 'number_input',
        validation: { min: 740, max: 3000, required: true },
      },

      // Arch-specific
      {
        id: 'opt-arch-radius',
        name: 'Radius (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Required for arch windows',
        isActive: true,
        order: 3,
        responsePath: 'window.measurements.archRadiusMm',
        inputType: 'number_input',
        validation: { min: 100, max: 2000, required: true },
        showWhen: { 'window.type': 'arch' },
        excludeFor: ['standard', 'coupled_2', 'coupled_3', 'coupled_4'],
      },
      {
        id: 'opt-arch-subtype-swept',
        name: 'Arch Type: Swept',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Swept arch style',
        isActive: true,
        order: 4,
        responsePath: 'window.subType',
        responseValue: 'Swept',
        inputType: 'single_select',
        showWhen: { 'window.type': 'arch' },
      },
      {
        id: 'opt-arch-subtype-gothic',
        name: 'Arch Type: Gothic',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Gothic arch style',
        isActive: true,
        order: 5,
        responsePath: 'window.subType',
        responseValue: 'Gothic',
        inputType: 'single_select',
        showWhen: { 'window.type': 'arch' },
      },
      {
        id: 'opt-arch-subtype-truearch',
        name: 'Arch Type: True Arch',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'True arch style',
        isActive: true,
        order: 6,
        responsePath: 'window.subType',
        responseValue: 'TrueArch',
        inputType: 'single_select',
        showWhen: { 'window.type': 'arch' },
      },

      // Bay-specific widths
      {
        id: 'opt-bay-window1-width',
        name: 'Bay Window 1 Width (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Width of first bay window',
        isActive: true,
        order: 7,
        responsePath: 'window.measurements.bayWidthsMm[0]',
        inputType: 'number_input',
        validation: { min: 370, max: 1600, required: true },
        showWhen: { 'window.type': { $regex: '^bay' } },
      },
      {
        id: 'opt-bay-window2-width',
        name: 'Bay Window 2 Width (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Width of second bay window',
        isActive: true,
        order: 8,
        responsePath: 'window.measurements.bayWidthsMm[1]',
        inputType: 'number_input',
        validation: { min: 370, max: 1600, required: true },
        showWhen: { 'window.type': { $regex: '^bay' } },
      },
      {
        id: 'opt-bay-window3-width',
        name: 'Bay Window 3 Width (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Width of third bay window',
        isActive: true,
        order: 9,
        responsePath: 'window.measurements.bayWidthsMm[2]',
        inputType: 'number_input',
        validation: { min: 370, max: 1600, required: true },
        showWhen: { 'window.type': { $regex: '^bay' } },
      },
      {
        id: 'opt-bay-window4-width',
        name: 'Bay Window 4 Width (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Width of fourth bay window',
        isActive: true,
        order: 10,
        responsePath: 'window.measurements.bayWidthsMm[3]',
        inputType: 'number_input',
        validation: { min: 370, max: 1600, required: true },
        showWhen: { 'window.type': { $regex: '^bay.*_4|.*_5' } },
      },
      {
        id: 'opt-bay-window5-width',
        name: 'Bay Window 5 Width (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Width of fifth bay window',
        isActive: true,
        order: 11,
        responsePath: 'window.measurements.bayWidthsMm[4]',
        inputType: 'number_input',
        validation: { min: 370, max: 1600, required: true },
        showWhen: { 'window.type': { $regex: '^bay.*_5' } },
      },

      // Transom
      {
        id: 'opt-transom',
        name: 'Transom Position (mm)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Transom position from bottom',
        isActive: true,
        order: 12,
        responsePath: 'window.measurements.transomMm',
        inputType: 'number_input',
        validation: { min: 100, max: 2500 },
      },
      {
        id: 'opt-transom-custom-percentage',
        name: 'Custom Split Percentage',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Custom split as percentage (0-100%)',
        isActive: true,
        order: 13,
        responsePath: 'window.measurements.customPercent',
        inputType: 'number_input',
        validation: { min: 0, max: 100 },
      },

      // Sash Function
      {
        id: 'opt-sash-function-fixed',
        name: 'Sash Function: Fixed',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Fixed sash (non-sliding)',
        isActive: true,
        order: 14,
        responsePath: 'window.sashFunction',
        responseValue: 'Fixed',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-sash-function-sliding',
        name: 'Sash Function: Sliding',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Sliding sash',
        isActive: true,
        order: 15,
        responsePath: 'window.sashFunction',
        responseValue: 'Sliding',
        inputType: 'single_select',
        validation: { required: true },
      },

      // Deep Bottom Rail Style
      {
        id: 'opt-style-deep-bottom-rail',
        name: 'Deep Bottom Rail Style',
        priceModifier: 35,
        priceType: 'fixed',
        description: 'Adds £35 - This is a STYLE option',
        isActive: true,
        order: 16,
        responsePath: 'window.style.deepBottomRail.enabled',
        responseValue: true,
        inputType: 'boolean',
      },

      // Horns (product-specific pricing via priceModifier)
      {
        id: 'opt-horns-standard',
        name: 'Add Horns',
        priceModifier: 28,
        priceType: 'fixed',
        description: 'Add horns +£28 for Standard/Arch',
        isActive: true,
        order: 17,
        responsePath: 'window.horns.enabled',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': { $in: ['standard', 'arch'] } },
      },
      {
        id: 'opt-horns-coupled-2',
        name: 'Add Horns (2 Windows)',
        priceModifier: 48,
        priceType: 'fixed',
        description: 'Add horns +£48',
        isActive: true,
        order: 18,
        responsePath: 'window.horns.enabled',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': 'coupled_2' },
      },
      {
        id: 'opt-horns-coupled-3',
        name: 'Add Horns (3 Windows)',
        priceModifier: 72,
        priceType: 'fixed',
        description: 'Add horns +£72',
        isActive: true,
        order: 19,
        responsePath: 'window.horns.enabled',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': 'coupled_3' },
      },
      {
        id: 'opt-horns-coupled-4',
        name: 'Add Horns (4 Windows)',
        priceModifier: 96,
        priceType: 'fixed',
        description: 'Add horns +£96',
        isActive: true,
        order: 20,
        responsePath: 'window.horns.enabled',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': 'coupled_4' },
      },
      {
        id: 'opt-horns-bay-3',
        name: 'Add Horns (Bay 3 Windows)',
        priceModifier: 96,
        priceType: 'fixed',
        description: 'Add horns +£96',
        isActive: true,
        order: 21,
        responsePath: 'window.horns.enabled',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': { $regex: '^bay.*_3' } },
      },
    ],
  },

  // ============================================
  // 3. PROFILE COLOURS (16 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-colors',
    type: CategoryType.COLOR,
    name: '🎨 Sash Profile Colours',
    description: 'Foils, spray finishes, and colour options - Select one group only',
    isActive: true,
    order: 2,
    options: [
      // Standard White
      {
        id: 'opt-standard-white',
        name: 'Standard White (PVC-U Finish)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Both sides smooth white PVC-U',
        isActive: true,
        order: 1,
        responsePath: 'window.profileColours.standardWhitePVCU.selected',
        responseValue: true,
        inputType: 'boolean',
      },

      // Standard Foils
      {
        id: 'opt-foil-external-only',
        name: 'Standard Foils: External only with PVC Internal',
        priceModifier: 70,
        priceType: 'fixed',
        description: 'Choose from standard foil swatches',
        isActive: true,
        order: 2,
        responsePath: 'window.profileColours.standardFoils.mode',
        responseValue: 'external_only',
        inputType: 'single_select',
      },
      {
        id: 'opt-foil-both-same',
        name: 'Standard Foils: Both Sides (same colour)',
        priceModifier: 70,
        priceType: 'fixed',
        description: 'Both sides same foil colour',
        isActive: true,
        order: 3,
        responsePath: 'window.profileColours.standardFoils.mode',
        responseValue: 'both_sides',
        inputType: 'single_select',
      },

      // Special Foils
      {
        id: 'opt-special-foil-internal',
        name: 'Special Foils: Internal Colour',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Select internal foil - Basket pricing',
        isActive: true,
        order: 4,
        responsePath: 'window.profileColours.specialFoils.internalSwatch',
        inputType: 'swatch_select',
      },
      {
        id: 'opt-special-foil-external',
        name: 'Special Foils: External Colour',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Select external foil - Must differ from internal',
        isActive: true,
        order: 5,
        responsePath: 'window.profileColours.specialFoils.externalSwatch',
        inputType: 'swatch_select',
        validation: { notEqual: 'window.profileColours.specialFoils.internalSwatch' },
      },

      // PVC-U White External
      {
        id: 'opt-pvc-white-external',
        name: 'PVC-U White (External only)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Pairs with internal foil',
        isActive: true,
        order: 6,
        responsePath: 'window.profileColours.externalPVCUWhiteOption',
        responseValue: true,
        inputType: 'boolean',
      },

      // Spray substrate
      {
        id: 'opt-spray-smooth-pvcu',
        name: 'Spray: On Smooth PVC-U',
        priceModifier: 135,
        priceType: 'fixed',
        description: 'Base spray cost £135',
        isActive: true,
        order: 7,
        responsePath: 'window.profileColours.spray.substrate',
        responseValue: 'smooth_pvcu',
        inputType: 'single_select',
      },
      {
        id: 'opt-spray-woodgrain-both',
        name: 'Spray: On Woodgrain both sides',
        priceModifier: 205,
        priceType: 'fixed',
        description: 'Base spray cost £205',
        isActive: true,
        order: 8,
        responsePath: 'window.profileColours.spray.substrate',
        responseValue: 'woodgrain_both',
        inputType: 'single_select',
      },
      {
        id: 'opt-spray-woodgrain-ext-foil-int',
        name: 'Spray: Woodgrain ext with internal foil',
        priceModifier: 205,
        priceType: 'fixed',
        description: 'Base spray cost £205',
        isActive: true,
        order: 9,
        responsePath: 'window.profileColours.spray.substrate',
        responseValue: 'woodgrain_ext_foil_int',
        inputType: 'single_select',
      },

      // Spray coverage
      {
        id: 'opt-spray-coverage-inside',
        name: 'Spray Coverage: Inside Only',
        priceModifier: 0,
        priceType: 'fixed',
        description: '1.0x multiplier',
        isActive: true,
        order: 10,
        responsePath: 'window.profileColours.spray.coverage',
        responseValue: 'inside_only',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.substrate': { $ne: null } },
      },
      {
        id: 'opt-spray-coverage-outside',
        name: 'Spray Coverage: Outside Only',
        priceModifier: 0,
        priceType: 'fixed',
        description: '1.0x multiplier',
        isActive: true,
        order: 11,
        responsePath: 'window.profileColours.spray.coverage',
        responseValue: 'outside_only',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.substrate': { $ne: null } },
      },
      {
        id: 'opt-spray-coverage-both',
        name: 'Spray Coverage: Both sides',
        priceModifier: 0,
        priceType: 'fixed',
        description: '1.5x multiplier',
        isActive: true,
        order: 12,
        responsePath: 'window.profileColours.spray.coverage',
        responseValue: 'both',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.substrate': { $ne: null } },
      },

      // Spray colour system inside
      {
        id: 'opt-spray-color-inside-ral',
        name: 'Inside Colour: RAL',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'RAL colour system',
        isActive: true,
        order: 13,
        responsePath: 'window.profileColours.spray.insideColourSystem',
        responseValue: 'RAL',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.coverage': { $in: ['inside_only', 'both'] } },
      },
      {
        id: 'opt-spray-color-inside-fb',
        name: 'Inside Colour: Farrow & Ball',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Farrow & Ball colour system',
        isActive: true,
        order: 14,
        responsePath: 'window.profileColours.spray.insideColourSystem',
        responseValue: 'FarrowAndBall',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.coverage': { $in: ['inside_only', 'both'] } },
      },

      // Spray colour system outside
      {
        id: 'opt-spray-color-outside-ral',
        name: 'Outside Colour: RAL',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'RAL colour system',
        isActive: true,
        order: 15,
        responsePath: 'window.profileColours.spray.outsideColourSystem',
        responseValue: 'RAL',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.coverage': { $in: ['outside_only', 'both'] } },
      },
      {
        id: 'opt-spray-color-outside-fb',
        name: 'Outside Colour: Farrow & Ball',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Farrow & Ball colour system',
        isActive: true,
        order: 16,
        responsePath: 'window.profileColours.spray.outsideColourSystem',
        responseValue: 'FarrowAndBall',
        inputType: 'single_select',
        showWhen: { 'window.profileColours.spray.coverage': { $in: ['outside_only', 'both'] } },
      },
    ],
  },

  // ============================================
  // 4. CILLS (7 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-cills',
    type: CategoryType.HARDWARE,
    name: '🪵 Cills',
    description: 'Cill sizes, horns, and bay returns',
    isActive: true,
    order: 3,
    options: [
      {
        id: 'opt-cill-152',
        name: 'Cill: 152mm',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Standard 152mm cill',
        isActive: true,
        order: 1,
        responsePath: 'window.cills.size',
        responseValue: '152mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-cill-185-white',
        name: 'Cill: 185mm White',
        priceModifier: 8,
        priceType: 'fixed',
        description: '+£8 for white smooth',
        isActive: true,
        order: 2,
        responsePath: 'window.cills.size',
        responseValue: '185mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-cill-185-colored',
        name: 'Cill: 185mm Coloured',
        priceModifier: 13,
        priceType: 'fixed',
        description: '+£13 for coloured',
        isActive: true,
        order: 3,
        responsePath: 'window.cills.size',
        responseValue: '185mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-cill-225-white',
        name: 'Cill: 225mm White',
        priceModifier: 11,
        priceType: 'fixed',
        description: '+£11 for white smooth',
        isActive: true,
        order: 4,
        responsePath: 'window.cills.size',
        responseValue: '225mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-cill-225-colored',
        name: 'Cill: 225mm Coloured',
        priceModifier: 17,
        priceType: 'fixed',
        description: '+£17 for coloured',
        isActive: true,
        order: 5,
        responsePath: 'window.cills.size',
        responseValue: '225mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-cill-horns',
        name: 'Cill Horns Size (each side)',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Numeric input in mm per side',
        isActive: true,
        order: 6,
        responsePath: 'window.cills.hornsSizeMmPerSide',
        inputType: 'number_input',
        validation: { min: 0, max: 500 },
      },
      // Note: Bay welded returns is auto-calculated, not a user question
    ],
  },

  // ============================================
  // 5. HARDWARE (25 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-hardware',
    type: CategoryType.HARDWARE,
    name: '🔧 Hardware',
    description: 'Hardware colours and optional extras',
    isActive: true,
    order: 4,
    options: [
      // Standard Hardware - all share same responsePath, different responseValue
      {
        id: 'opt-hw-standard-white',
        name: 'Standard Hardware: White',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 1,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'White',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-gold',
        name: 'Standard Hardware: Gold',
        priceModifier: 17,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 2,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Gold',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-black',
        name: 'Standard Hardware: Black',
        priceModifier: 17,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 3,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Black',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-chrome',
        name: 'Standard Hardware: Chrome',
        priceModifier: 17,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 4,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Chrome',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-satin',
        name: 'Standard Hardware: Satin Chrome',
        priceModifier: 17,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 5,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Satin Chrome',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-antique',
        name: 'Standard Hardware: Antique Gold',
        priceModifier: 22,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 6,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Antique Gold',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-rose',
        name: 'Standard Hardware: Rose Gold',
        priceModifier: 22,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 7,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Rose Gold',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-hw-standard-blacknickel',
        name: 'Standard Hardware: Black Nickel',
        priceModifier: 22,
        priceType: 'fixed',
        description: 'Includes camlocks and keeps',
        isActive: true,
        order: 8,
        responsePath: 'window.hardware.colour.name',
        responseValue: 'Black Nickel',
        inputType: 'single_select',
        validation: { required: true },
      },

      // Custom Hardware toggle
      {
        id: 'opt-hw-custom-enabled',
        name: 'Use Custom Hardware',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Enable custom hardware option',
        isActive: true,
        order: 9,
        responsePath: 'window.hardware.customHardware.enabled',
        responseValue: true,
        inputType: 'boolean',
      },

      // D-Handles - Enable first, then select tier
      {
        id: 'opt-dhandles-enabled',
        name: 'Add D-Handles',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Enable D-Handles',
        isActive: true,
        order: 10,
        responsePath: 'window.hardware.optionalExtras.dHandles.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
      {
        id: 'opt-dhandle-standard-tier',
        name: 'D-Handles: Standard Tier',
        priceModifier: 6,
        priceType: 'fixed',
        description: 'Standard colours £6',
        isActive: true,
        order: 11,
        responsePath: 'window.hardware.optionalExtras.dHandles.tier',
        responseValue: 'standard',
        inputType: 'single_select',
        showWhen: { 'window.hardware.optionalExtras.dHandles.enabled': true },
      },
      {
        id: 'opt-dhandle-premium-tier',
        name: 'D-Handles: Premium Tier',
        priceModifier: 9,
        priceType: 'fixed',
        description: 'Premium colours £9',
        isActive: true,
        order: 12,
        responsePath: 'window.hardware.optionalExtras.dHandles.tier',
        responseValue: 'premium',
        inputType: 'single_select',
        showWhen: { 'window.hardware.optionalExtras.dHandles.enabled': true },
      },

      // Finger Lifts
      {
        id: 'opt-finger-lifts',
        name: 'Inset Finger Lifts',
        priceModifier: 10,
        priceType: 'fixed',
        description: 'Replaces sash lifts - Matches hardware colour',
        isActive: true,
        order: 13,
        responsePath: 'window.hardware.optionalExtras.insetFingerLifts.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
    ],
  },

  // ============================================
  // 6. SECURITY (5 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-security',
    type: CategoryType.HARDWARE,
    name: '🔒 Security',
    description: 'Security levels',
    isActive: true,
    order: 5,
    options: [
      {
        id: 'opt-sec-standard',
        name: 'Standard',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Standard security',
        isActive: true,
        order: 1,
        responsePath: 'window.security',
        responseValue: 'Standard',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-sec-non-locking',
        name: 'Standard Non Locking',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Non-locking',
        isActive: true,
        order: 2,
        responsePath: 'window.security',
        responseValue: 'Standard Non Locking',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-sec-upgraded',
        name: 'Upgraded Security Lock',
        priceModifier: 18,
        priceType: 'fixed',
        description: '+£18',
        isActive: true,
        order: 3,
        responsePath: 'window.security',
        responseValue: 'Upgraded Security Lock',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-sec-pas24',
        name: 'PAS 24 / Document Q',
        priceModifier: 85,
        priceType: 'fixed',
        description: '+£85',
        isActive: true,
        order: 4,
        responsePath: 'window.security',
        responseValue: 'PAS 24 / Document Q',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-sec-sbd',
        name: 'Secured by Design',
        priceModifier: 115,
        priceType: 'fixed',
        description: '+£115',
        isActive: true,
        order: 5,
        responsePath: 'window.security',
        responseValue: 'Secured by Design',
        inputType: 'single_select',
        validation: { required: true },
      },
    ],
  },

  // ============================================
  // 7. DECORATIVE BEADS (7 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-decorative',
    type: CategoryType.STYLE,
    name: '✨ Decorative Beads',
    description: 'Bar types and configurations',
    isActive: true,
    order: 6,
    options: [
      {
        id: 'opt-bar-none',
        name: 'Bar Type: None',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'No decorative bars',
        isActive: true,
        order: 1,
        responsePath: 'window.decorativeBeads.type',
        responseValue: 'None',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-bar-astragal',
        name: 'Bar Type: Astragal',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Matrix pricing',
        isActive: true,
        order: 2,
        responsePath: 'window.decorativeBeads.type',
        responseValue: 'Astragal',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-bar-georgian-18',
        name: 'Bar Type: Georgian 18mm',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Matrix pricing',
        isActive: true,
        order: 3,
        responsePath: 'window.decorativeBeads.type',
        responseValue: 'Georgian 18mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-bar-georgian-25',
        name: 'Bar Type: Georgian 25mm',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Matrix pricing',
        isActive: true,
        order: 4,
        responsePath: 'window.decorativeBeads.type',
        responseValue: 'Georgian 25mm',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-bar-lead',
        name: 'Bar Type: Lead',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Matrix pricing',
        isActive: true,
        order: 5,
        responsePath: 'window.decorativeBeads.type',
        responseValue: 'Lead',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-bar-config-top-sash',
        name: 'Top Sash Bar Configuration',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Visual selector',
        isActive: true,
        order: 6,
        responsePath: 'window.decorativeBeads.topSashBars',
        inputType: 'visual_select',
        showWhen: { 'window.decorativeBeads.type': { $ne: 'None' } },
      },
      {
        id: 'opt-bar-config-bottom-sash',
        name: 'Bottom Sash Bar Configuration',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Visual selector',
        isActive: true,
        order: 7,
        responsePath: 'window.decorativeBeads.bottomSashBars',
        inputType: 'visual_select',
        showWhen: { 'window.decorativeBeads.type': { $ne: 'None' } },
      },
    ],
  },

  // ============================================
  // 8. TRICKLE VENTS (2 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-vents',
    type: CategoryType.HARDWARE,
    name: '🌬️ Trickle Vents',
    description: 'Ventilation options - Default enabled',
    isActive: true,
    order: 7,
    options: [
      {
        id: 'opt-vent-enabled',
        name: 'Enable Trickle Vents',
        priceModifier: 14,
        priceType: 'fixed',
        description: 'Default TRUE - £14 per window',
        isActive: true,
        order: 1,
        responsePath: 'window.trickleVents.enabled',
        responseValue: true,
        inputType: 'boolean',
        defaultValue: true,
        excludeFor: ['arch'],
      },
      {
        id: 'opt-vent-quantity',
        name: 'Trickle Vents Quantity',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Number of windows',
        isActive: true,
        order: 2,
        responsePath: 'window.trickleVents.quantity',
        inputType: 'number_input',
        validation: { min: 0, max: 10 },
        showWhen: { 'window.trickleVents.enabled': true },
      },
    ],
  },

  // ============================================
  // 9. GLAZING (10 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-glazing',
    type: CategoryType.GLASS,
    name: '🔲 Glass Options (Mandatory)',
    description: 'Glass specifications and spacer bars',
    isActive: true,
    order: 8,
    options: [
      {
        id: 'opt-glass-unglazed',
        name: 'Unglazed',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'No glass fitted',
        isActive: true,
        order: 1,
        responsePath: 'window.glazing.option',
        responseValue: 'Unglazed',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-glass-u13',
        name: '1.3 U-Value',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Standard (toughened)',
        isActive: true,
        order: 2,
        responsePath: 'window.glazing.uValue',
        responseValue: '1.3',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-glass-u12',
        name: '1.2 U-Value',
        priceModifier: 17,
        priceType: 'fixed',
        description: '+£17 (toughened)',
        isActive: true,
        order: 3,
        responsePath: 'window.glazing.uValue',
        responseValue: '1.2',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-glass-obscure',
        name: 'Add Obscure Glass',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Enable obscure glass',
        isActive: true,
        order: 4,
        responsePath: 'window.glazing.obscure.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
      {
        id: 'opt-glass-obscure-top',
        name: 'Obscure: Top Sash',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Top sash only',
        isActive: true,
        order: 5,
        responsePath: 'window.glazing.obscure.placement',
        responseValue: 'Top',
        inputType: 'single_select',
        showWhen: { 'window.glazing.obscure.enabled': true },
      },
      {
        id: 'opt-glass-obscure-bottom',
        name: 'Obscure: Bottom Sash',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Bottom sash only',
        isActive: true,
        order: 6,
        responsePath: 'window.glazing.obscure.placement',
        responseValue: 'Bottom',
        inputType: 'single_select',
        showWhen: { 'window.glazing.obscure.enabled': true },
      },
      {
        id: 'opt-glass-obscure-both',
        name: 'Obscure: Both Sashes',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Both sashes',
        isActive: true,
        order: 7,
        responsePath: 'window.glazing.obscure.placement',
        responseValue: 'Both',
        inputType: 'single_select',
        showWhen: { 'window.glazing.obscure.enabled': true },
      },
      {
        id: 'opt-glass-laminated',
        name: 'Laminated/Acoustic',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 8,
        responsePath: 'window.glazing.laminated',
        responseValue: true,
        inputType: 'boolean',
      },
      {
        id: 'opt-spacer-black',
        name: 'Spacer Bar: Black',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Standard',
        isActive: true,
        order: 9,
        responsePath: 'window.spacerBar.colour',
        responseValue: 'Black',
        inputType: 'single_select',
        validation: { required: true },
      },
      {
        id: 'opt-spacer-white',
        name: 'Spacer Bar: White',
        priceModifier: 11,
        priceType: 'fixed',
        description: '+£11 per window',
        isActive: true,
        order: 10,
        responsePath: 'window.spacerBar.colour',
        responseValue: 'White',
        inputType: 'single_select',
        validation: { required: true },
      },
    ],
  },

  // ============================================
  // 10. ADD ONS (2 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-addons',
    type: CategoryType.HARDWARE,
    name: '➕ Add Ons',
    description: 'Trim options',
    isActive: true,
    order: 9,
    options: [
      {
        id: 'opt-trim-25mm',
        name: '25mm Trim',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 1,
        responsePath: 'window.addOns.trim25mm.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
      {
        id: 'opt-trim-40mm',
        name: '40mm Trim',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 2,
        responsePath: 'window.addOns.trim40mm.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
    ],
  },

  // ============================================
  // 11. OPTIONAL EXTRAS (14 options) - COMPLETE CONFIGURATION
  // ============================================
  {
    id: 'cat-optional-extras',
    type: CategoryType.HARDWARE,
    name: '⭐ Optional Extras',
    description: 'Weather bar, egress, trim components',
    isActive: true,
    order: 10,
    options: [
      {
        id: 'opt-weather-bar',
        name: 'Weather Bar',
        priceModifier: 15,
        priceType: 'fixed',
        description: '+£15',
        isActive: true,
        order: 1,
        responsePath: 'window.optionalExtras.weatherBar.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
      {
        id: 'opt-loose-hardware',
        name: 'Loose Hardware',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 2,
        responsePath: 'window.optionalExtras.looseHardware.enabled',
        responseValue: true,
        inputType: 'boolean',
      },
      {
        id: 'opt-egress',
        name: 'Egress Compliant',
        priceModifier: 0,
        priceType: 'fixed',
        description: '£0',
        isActive: true,
        order: 3,
        responsePath: 'window.optionalExtras.egressCompliant.enabled',
        responseValue: true,
        inputType: 'boolean',
      },

      // Head
      {
        id: 'opt-head-25mm',
        name: 'Head: 25mm thickness',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 4,
        responsePath: 'window.optionalExtras.head.thickness',
        responseValue: '25mm',
        inputType: 'single_select',
      },
      {
        id: 'opt-head-40mm',
        name: 'Head: 40mm thickness',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 5,
        responsePath: 'window.optionalExtras.head.thickness',
        responseValue: '40mm',
        inputType: 'single_select',
      },
      {
        id: 'opt-head-quantity',
        name: 'Head: Quantity',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Front & back',
        isActive: true,
        order: 6,
        responsePath: 'window.optionalExtras.head.quantity',
        inputType: 'number_input',
        validation: { min: 0, max: 10 },
        showWhen: { 'window.optionalExtras.head.thickness': { $ne: null } },
      },

      // Cill
      {
        id: 'opt-cill-extra-25mm',
        name: 'Cill Extra: 25mm thickness',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 8,
        responsePath: 'window.optionalExtras.cill.thickness',
        responseValue: '25mm',
        inputType: 'single_select',
      },
      {
        id: 'opt-cill-extra-40mm',
        name: 'Cill Extra: 40mm thickness',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 9,
        responsePath: 'window.optionalExtras.cill.thickness',
        responseValue: '40mm',
        inputType: 'single_select',
      },
      {
        id: 'opt-cill-extra-quantity',
        name: 'Cill Extra: Quantity',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Front & back',
        isActive: true,
        order: 10,
        responsePath: 'window.optionalExtras.cill.quantity',
        inputType: 'number_input',
        validation: { min: 0, max: 10 },
        showWhen: { 'window.optionalExtras.cill.thickness': { $ne: null } },
      },

      // RHJ
      {
        id: 'opt-rhj-25mm',
        name: 'RHJ: 25mm thickness',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 12,
        responsePath: 'window.optionalExtras.rhj.thickness',
        responseValue: '25mm',
        inputType: 'single_select',
      },
      {
        id: 'opt-rhj-40mm',
        name: 'RHJ: 40mm thickness',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Price TBC',
        isActive: true,
        order: 13,
        responsePath: 'window.optionalExtras.rhj.thickness',
        responseValue: '40mm',
        inputType: 'single_select',
      },
      {
        id: 'opt-rhj-quantity',
        name: 'RHJ: Quantity',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Front & back',
        isActive: true,
        order: 14,
        responsePath: 'window.optionalExtras.rhj.quantity',
        inputType: 'number_input',
        validation: { min: 0, max: 10 },
        showWhen: { 'window.optionalExtras.rhj.thickness': { $ne: null } },
      },

      // Bay-specific
      {
        id: 'opt-bay-structural-posts',
        name: 'Structural Bay Posts',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Box Bay only - Price TBC',
        isActive: true,
        order: 15,
        responsePath: 'window.optionalExtras.bayStructuralPosts',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': { $regex: '^bay_box' } },
      },
      {
        id: 'opt-bay-jacks',
        name: 'Bay Jacks',
        priceModifier: 0,
        priceType: 'fixed',
        description: 'Box Bay only - Price TBC',
        isActive: true,
        order: 16,
        responsePath: 'window.optionalExtras.bayJacks',
        responseValue: true,
        inputType: 'boolean',
        showWhen: { 'window.type': { $regex: '^bay_box' } },
      },
    ],
  },
];
